/**
 * Three.js manager that renders a cursor per remote user.
 *
 * Look: a small mouse-pointer arrow in the user's color, with their
 * initials as a chip to the right (single canvas, single sprite). The
 * arrow's tip — not the sprite's center — sits on the world-space hover
 * point, achieved via `sprite.center`.
 *
 * Positions are lerped between updates so the 10 Hz network stream looks
 * smooth on a 60 Hz frame loop. Stale cursors (no update for ≥ 3 s) fade.
 */

import {
  Color,
  Group,
  PerspectiveCamera,
  Scene as ThreeScene,
  Sprite,
  SpriteMaterial,
  Texture,
  Vector3,
} from 'three'

import type { PresenceMember, RemoteCursor } from './types'

const STALE_MS = 3_000

// Distance from camera (metres) at which the sprite's scale matches its
// authored `baseW` / `baseH`. We rescale per frame relative to this so the
// cursor stays at roughly the same screen-pixel size regardless of zoom.
const REFERENCE_DISTANCE_M = 8
// Don't let the screen-constant pass make the cursor disappear (very far)
// or fill the viewport (very close).
const MIN_SCALE_FACTOR = 0.35
const MAX_SCALE_FACTOR = 3.0

interface CursorEntry {
  group: Group
  sprite: Sprite
  baseW: number
  baseH: number
  current: Vector3
  target: Vector3
  lastUpdate: number
  offscreen: boolean
}

export class CursorsManager {
  private entries = new Map<string, CursorEntry>()
  private membersById = new Map<string, PresenceMember>()

  constructor(private readonly scene: ThreeScene) {}

  /** Updates the lookup of who's who — call when presence list changes. */
  setMembers(members: PresenceMember[]): void {
    this.membersById = new Map(members.map((m) => [m.id, m]))
    for (const [userId, entry] of this.entries) {
      if (!this.membersById.has(userId)) {
        this.disposeEntry(entry)
        this.entries.delete(userId)
      }
    }
  }

  /** Sync from the remoteCursors map exposed by RealtimeProvider. */
  sync(remote: Map<string, RemoteCursor>): void {
    for (const [userId, c] of remote) {
      const member = this.membersById.get(userId)
      if (!member) continue
      let entry = this.entries.get(userId)
      const fresh = entry == null
      if (!entry) {
        entry = this.createEntry(member)
        this.entries.set(userId, entry)
      }
      if (c.p) {
        entry.target.set(c.p.x, c.p.y, c.p.z)
        if (fresh || entry.offscreen) entry.current.copy(entry.target)
        entry.offscreen = false
      } else {
        entry.offscreen = true
      }
      entry.lastUpdate = c.t || Date.now()
      entry.group.visible = !entry.offscreen
    }
    for (const [userId, entry] of this.entries) {
      if (!remote.has(userId)) entry.group.visible = false
    }
  }

  /** Call from the render loop — animates lerp + stale fade-out + the
   * screen-constant scaling that keeps the cursor a sensible size when
   * the user zooms in. */
  tick(camera: PerspectiveCamera): void {
    const now = Date.now()
    for (const entry of this.entries.values()) {
      if (!entry.group.visible) continue
      entry.current.lerp(entry.target, 0.35)
      entry.group.position.copy(entry.current)

      // Compensate for perspective shrink: when the cursor is closer than
      // REFERENCE_DISTANCE_M, scale it down so it doesn't fill the screen;
      // when it's further, scale up so it doesn't vanish.
      const dist = camera.position.distanceTo(entry.current)
      const factor = Math.max(
        MIN_SCALE_FACTOR,
        Math.min(MAX_SCALE_FACTOR, dist / REFERENCE_DISTANCE_M),
      )
      entry.sprite.scale.set(entry.baseW * factor, entry.baseH * factor, 1)

      const age = now - entry.lastUpdate
      const fadeT = age <= STALE_MS ? 1 : Math.max(0, 1 - (age - STALE_MS) / 1000)
      ;(entry.sprite.material as SpriteMaterial).opacity = fadeT
    }
  }

  dispose(): void {
    for (const entry of this.entries.values()) this.disposeEntry(entry)
    this.entries.clear()
  }

  private createEntry(member: PresenceMember): CursorEntry {
    const group = new Group()
    group.name = `cursor-${member.id}`
    group.visible = false

    const sprite = buildPointerSprite(member.initials, member.color)
    group.add(sprite)

    this.scene.add(group)
    return {
      group,
      sprite,
      baseW: sprite.scale.x,
      baseH: sprite.scale.y,
      current: new Vector3(),
      target: new Vector3(),
      lastUpdate: 0,
      offscreen: true,
    }
  }

  private disposeEntry(entry: CursorEntry): void {
    this.scene.remove(entry.group)
    entry.group.traverse((node) => {
      const mat = (node as Sprite).material as SpriteMaterial | undefined
      if (mat?.map) mat.map.dispose()
      mat?.dispose?.()
    })
  }
}

/**
 * Build the cursor as a single canvas sprite:
 *
 *      ▲        ┌────┐
 *     ▲ ▲       │ BA │
 *    ▲   ▲      └────┘
 *   ▲     ▲
 *  ▲───────
 *
 * The arrow tip is at canvas (~3, ~3) CSS pixels. We set `sprite.center`
 * to that UV coordinate so the tip sits exactly on the world hover point.
 */
function buildPointerSprite(initials: string, color: number): Sprite {
  const dpr = Math.min(window.devicePixelRatio || 1, 2)

  // Layout (CSS px). Tip near top-left, chip to the right of the arrow.
  const arrowWidthCss = 18
  const arrowHeightCss = 22
  const gapCss = 4
  const chipFontCss = 11
  const chipPaddingCss = 4
  const chipHeightCss = chipFontCss + chipPaddingCss * 2
  const chipFontPx = Math.round(chipFontCss * dpr)

  const measure = document.createElement('canvas').getContext('2d')!
  measure.font = `bold ${chipFontPx}px -apple-system, "SF Pro Text", sans-serif`
  const chipTextWidthPx = Math.ceil(measure.measureText(initials).width)
  const chipWidthCss = Math.max(20, Math.ceil(chipTextWidthPx / dpr) + chipPaddingCss * 2)

  const canvasWidthCss = arrowWidthCss + gapCss + chipWidthCss + 2
  const canvasHeightCss = Math.max(arrowHeightCss, chipHeightCss) + 4

  const canvas = document.createElement('canvas')
  canvas.width = canvasWidthCss * dpr
  canvas.height = canvasHeightCss * dpr
  const ctx = canvas.getContext('2d')!

  const colorHex = '#' + new Color(color).getHexString()

  // ── Arrow ─────────────────────────────────────────────────────────────
  // Classic OS pointer silhouette. Coordinates in CSS px first, scaled by
  // dpr when drawing. Tip is the sprite's anchor (see sprite.center below).
  const tip = { x: 3, y: 3 }
  ctx.fillStyle = colorHex
  ctx.strokeStyle = '#ffffff'
  ctx.lineWidth = 1.5 * dpr
  ctx.lineJoin = 'round'
  ctx.beginPath()
  // Use an offset pen so the stroke (white outline) sits crisply outside
  // the colored fill — Path is the visible arrow silhouette.
  const px = (x: number) => x * dpr
  const py = (y: number) => y * dpr
  ctx.moveTo(px(tip.x), py(tip.y))
  ctx.lineTo(px(tip.x), py(tip.y + 16))
  ctx.lineTo(px(tip.x + 4), py(tip.y + 12))
  ctx.lineTo(px(tip.x + 7), py(tip.y + 18))
  ctx.lineTo(px(tip.x + 9), py(tip.y + 17))
  ctx.lineTo(px(tip.x + 6), py(tip.y + 11))
  ctx.lineTo(px(tip.x + 11), py(tip.y + 11))
  ctx.closePath()
  ctx.fill()
  ctx.stroke()

  // ── Initials chip ─────────────────────────────────────────────────────
  const chipX = px(arrowWidthCss + gapCss)
  const chipY = py(2)
  const chipW = px(chipWidthCss)
  const chipH = py(chipHeightCss)
  ctx.fillStyle = colorHex
  roundedRect(ctx, chipX, chipY, chipW, chipH, 4 * dpr)
  ctx.fill()
  ctx.strokeStyle = 'rgba(255,255,255,0.95)'
  ctx.lineWidth = Math.max(1, dpr)
  ctx.stroke()
  ctx.font = `bold ${chipFontPx}px -apple-system, "SF Pro Text", sans-serif`
  ctx.fillStyle = '#ffffff'
  ctx.textBaseline = 'middle'
  ctx.textAlign = 'center'
  ctx.fillText(initials, chipX + chipW / 2, chipY + chipH / 2 + 1)

  // ── Sprite ────────────────────────────────────────────────────────────
  const tex = new Texture(canvas)
  tex.needsUpdate = true
  const mat = new SpriteMaterial({
    map: tex,
    transparent: true,
    sizeAttenuation: true,
    depthTest: false,
    depthWrite: false,
  })
  const sprite = new Sprite(mat)
  // Anchor at the arrow tip in UV space: canvas (tip.x, tip.y) → UV (u, 1-v).
  sprite.center.set(tip.x / canvasWidthCss, 1 - tip.y / canvasHeightCss)
  // World-units height calibrated so that at REFERENCE_DISTANCE_M (=8 m)
  // the cursor reads as ~30 px tall on a 1080p viewport. Closer + farther
  // are then compensated per-frame by `tick(camera)` in the manager.
  const baseH = 0.22
  const baseW = baseH * (canvasWidthCss / canvasHeightCss)
  sprite.scale.set(baseW, baseH, 1)
  sprite.renderOrder = 10_000
  return sprite
}

function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.lineTo(x + w - r, y)
  ctx.quadraticCurveTo(x + w, y, x + w, y + r)
  ctx.lineTo(x + w, y + h - r)
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h)
  ctx.lineTo(x + r, y + h)
  ctx.quadraticCurveTo(x, y + h, x, y + h - r)
  ctx.lineTo(x, y + r)
  ctx.quadraticCurveTo(x, y, x + r, y)
  ctx.closePath()
}
